import React from "react";
import { motion } from "framer-motion";
import { AURA_COLORS } from "../lib/aura";

/**
 * Stylised Human Design bodygraph — 9 centers + simplified connecting channels.
 * Color glows based on aura type. Not anatomically exact gates — this is a
 * Phase 1 visual signature suitable for the "aha moment".
 */
export const Bodygraph = ({ hdType = "Generator", definedCenters = [], className = "" }) => {
  const color = AURA_COLORS[hdType] || AURA_COLORS.Generator;
  const definedSet = new Set(definedCenters);

  // Centers: head, ajna, throat, g (self), heart, sacral, solar plexus, spleen, root
  const centers = [
    { id: "head", shape: "tri-up", x: 150, y: 35, label: "Head" },
    { id: "ajna", shape: "tri-down", x: 150, y: 100, label: "Ajna" },
    { id: "throat", shape: "square", x: 150, y: 165, label: "Throat" },
    { id: "g", shape: "diamond", x: 150, y: 240, label: "G" },
    { id: "heart", shape: "tri-left", x: 215, y: 245, label: "Heart" },
    { id: "spleen", shape: "tri-right", x: 65, y: 320, label: "Spleen" },
    { id: "sacral", shape: "square", x: 150, y: 335, label: "Sacral" },
    { id: "solar", shape: "tri-left", x: 235, y: 320, label: "Solar Plexus" },
    { id: "root", shape: "square", x: 150, y: 420, label: "Root" },
  ];

  // simplified channels (lines between centers)
  const channels = [
    ["head", "ajna"],
    ["ajna", "throat"],
    ["throat", "g"],
    ["throat", "heart"],
    ["g", "sacral"],
    ["g", "spleen"],
    ["heart", "solar"],
    ["sacral", "root"],
    ["spleen", "root"],
    ["solar", "root"],
  ];

  const ptMap = Object.fromEntries(centers.map((c) => [c.id, c]));

  const renderShape = (c, i) => {
    const isDefined = definedSet.has(c.id);
    const fill = isDefined ? `${color}CC` : "rgba(255,255,255,0.04)";
    const stroke = isDefined ? color : "rgba(255,255,255,0.25)";
    const size = 46;
    const props = {
      fill,
      stroke,
      strokeWidth: isDefined ? 2 : 1,
      style: isDefined ? { filter: `drop-shadow(0 0 18px ${color})` } : {},
    };
    if (c.shape === "tri-up") {
      const pts = `${c.x},${c.y - size / 2} ${c.x - size / 2},${c.y + size / 2} ${c.x + size / 2},${c.y + size / 2}`;
      return <motion.polygon initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} points={pts} {...props} />;
    }
    if (c.shape === "tri-down") {
      const pts = `${c.x},${c.y + size / 2} ${c.x - size / 2},${c.y - size / 2} ${c.x + size / 2},${c.y - size / 2}`;
      return <motion.polygon initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} points={pts} {...props} />;
    }
    if (c.shape === "tri-left") {
      const pts = `${c.x - size / 2},${c.y} ${c.x + size / 2},${c.y - size / 2} ${c.x + size / 2},${c.y + size / 2}`;
      return <motion.polygon initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} points={pts} {...props} />;
    }
    if (c.shape === "tri-right") {
      const pts = `${c.x + size / 2},${c.y} ${c.x - size / 2},${c.y - size / 2} ${c.x - size / 2},${c.y + size / 2}`;
      return <motion.polygon initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} points={pts} {...props} />;
    }
    if (c.shape === "diamond") {
      const pts = `${c.x},${c.y - size / 2} ${c.x + size / 2},${c.y} ${c.x},${c.y + size / 2} ${c.x - size / 2},${c.y}`;
      return <motion.polygon initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }} points={pts} {...props} />;
    }
    // square
    return (
      <motion.rect
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.07 }}
        x={c.x - size / 2}
        y={c.y - size / 2}
        width={size}
        height={size}
        rx={6}
        {...props}
      />
    );
  };

  return (
    <div className={`relative ${className}`} data-testid="bodygraph">
      <motion.svg
        viewBox="0 0 300 480"
        width="100%"
        height="100%"
        className="animate-aura"
        style={{ color }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* channels */}
        {channels.map(([a, b], i) => (
          <motion.line
            key={`${a}-${b}`}
            x1={ptMap[a].x}
            y1={ptMap[a].y}
            x2={ptMap[b].x}
            y2={ptMap[b].y}
            stroke={`${color}55`}
            strokeWidth="1.5"
            strokeDasharray="4 4"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 0.2 + i * 0.08, duration: 0.8 }}
          />
        ))}
        {centers.map((c, i) => (
          <g key={c.id}>{renderShape(c, i)}</g>
        ))}
      </motion.svg>
    </div>
  );
};
